import type { ButtonHTMLAttributes, JSX, ReactNode } from 'react';
import { Spinner } from './Spinner';
import { CONTROL_HEIGHT, FOCUS_RING, cx } from './styles';

export type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps
  extends Omit<
    ButtonHTMLAttributes<HTMLButtonElement>,
    'children' | 'className' | 'disabled' | 'type'
  > {
  children?: ReactNode;
  className?: string;
  /** 默认 `button`：表单里忘写 type 会变成隐式提交，这是最常见的"点一下整页提交"事故 */
  type?: 'button' | 'submit' | 'reset';
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** 忙碌态：暂时不接受点击，但**仍在流程里**（所以只加 `aria-busy`，不换文字） */
  loading?: boolean;
  disabled?: boolean;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
  /** 撑满可用宽度。移动端与侧栏里的主操作需要它 */
  block?: boolean;
}

const VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'border-transparent bg-accent text-accent-fg shadow-[0_1px_2px_rgb(9_30_66/0.12)] hover:bg-accent-hover active:bg-accent-hover',
  secondary: 'border-line-strong bg-surface-1 text-fg hover:border-accent hover:bg-surface-2',
  /*
   * 危险变体 —— 使用边界（重要）：
   *
   *   用：动作一旦下发就**难以挽回**，或者会**改变机体行为**。
   *       解锁/上锁、写入参数并重启、清除全部航点、强制降落。
   *   不用：普通删除、取消、关闭、返回。危险色用滥了就等于没有危险色，
   *       而真正危险的那一刻用户已经没有视觉警报可用了。
   *
   * 另外：危险动作**只改颜色是不够的**，调用方必须配一次确认（确认气泡或输入名称确认）。
   * 只靠颜色，等于把安全边界交给用户的眼睛。
   *
   * 文字色用 `fg-inverted` 而不是写死白色：深色主题下 `critical` 是**浅**红，
   * 白字会糊掉；`fg-inverted` 会跟着主题翻转。
   */
  danger: 'border-transparent bg-critical text-fg-inverted hover:brightness-110 active:brightness-95',
  ghost: 'border-transparent bg-transparent text-fg-muted hover:bg-surface-3 hover:text-fg',
};

const SIZES: Record<ButtonSize, string> = {
  sm: 'gap-1 px-2 text-xs',
  md: 'gap-2 px-3 text-sm',
  lg: 'gap-2 px-4 text-sm',
};

export function Button({
  variant = 'secondary',
  size = 'md',
  loading = false,
  block = false,
  disabled = false,
  iconLeft,
  iconRight,
  type = 'button',
  className,
  children,
  ...rest
}: ButtonProps): JSX.Element {
  return (
    <button
      {...rest}
      type={type}
      /*
       * 忙碌时用原生 `disabled` 而不是只加 `aria-disabled`：后者拦不住第二次点击，
       * 而"连点两次下发"在 GCS 里会真实造成重复动作。
       */
      disabled={disabled || loading}
      aria-busy={loading ? true : undefined}
      className={cx(
        'inline-flex shrink-0 select-none items-center justify-center rounded-control border font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50',
        CONTROL_HEIGHT[size],
        SIZES[size],
        VARIANTS[variant],
        FOCUS_RING,
        block && 'w-full',
        className,
      )}
    >
      {/*
       * 忙碌时把**图标位**换成转圈，而不是把文字换成"加载中"：
       * 文字一换按钮宽度就变，用户的手指/光标会落到隔壁按钮上。
       */}
      {loading ? (
        <Spinner size="sm" />
      ) : iconLeft === undefined ? null : (
        <span aria-hidden="true" className="inline-flex shrink-0 items-center">
          {iconLeft}
        </span>
      )}
      {children}
      {iconRight === undefined ? null : (
        <span aria-hidden="true" className="inline-flex shrink-0 items-center">
          {iconRight}
        </span>
      )}
    </button>
  );
}
